import { type ForwardedRef, type PropsWithChildren } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import {
  CustomTreeData,
  type GridColumnExtension,
  TreeDataState,
  DataTypeProvider,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
  TableTreeColumn,
} from "@devexpress/dx-react-grid-bootstrap4";
import { useMMMTableExpandedRows } from "@features/mmm/hooks/mmm-url-state";
import { type BudgetMixBreakdownSpendDatum } from "@north-beam/nb-common";

import { TableTreeColumnExpandButton } from "../../shared/table-components/table-tree-column-expand-button";
import { BUDGET_MIX_WIDTH_WITH_CHART } from "../budget-mix-chart/constants";

import { ControllerBreakdownFormatter } from "./controller-breakown-formatter";
import { TableTreeColumnIndent } from "./table-tree-column-indent";
import { getChildRows, getRows } from "./utils";

import "./mmm-budget-mix-table.scss";

export const BudgetMixControllerTable = ({
  tableData,
  onExpandRows,
}: {
  tableData: BudgetMixBreakdownSpendDatum[];
  onExpandRows: (expandedRowIds: (string | number)[]) => void;
}) => {
  const { user } = useUser();
  const columnExtensions: GridColumnExtension[] = [
    {
      columnName: "label",
      width: BUDGET_MIX_WIDTH_WITH_CHART,
      align: "right",
    },
  ];
  const [expandedRows] = useMMMTableExpandedRows();

  const columns = [
    {
      name: "label",
      title: "Breakdown",
    },
  ];
  const rows = getRows(tableData, user);

  const numOfBreakdowns = tableData[0]?.breakdowns.length ?? 0;
  const isExpandable = numOfBreakdowns > 1;

  return (
    <div
      className="h-full p-0 m-0 mmm-budget-mix-table "
      style={{ width: `${BUDGET_MIX_WIDTH_WITH_CHART}px` }}
    >
      <Grid rows={rows} columns={columns}>
        <TreeDataState
          onExpandedRowIdsChange={onExpandRows}
          expandedRowIds={expandedRows}
        />
        <DataTypeProvider
          for={["label"]}
          formatterComponent={(props) => (
            <ControllerBreakdownFormatter {...props} />
          )}
        />
        <CustomTreeData getChildRows={getChildRows} />
        <Table columnExtensions={columnExtensions} /> <TableHeaderRow />
        <TableTreeColumn
          for="label"
          cellComponent={BudgetMixTableCell}
          indentComponent={TableTreeColumnIndent}
          expandButtonComponent={(props) => (
            <TableTreeColumnExpandButton
              visible={props.visible}
              onToggle={props.onToggle}
              expanded={props.expanded}
              isExpandable={isExpandable}
            />
          )}
        />
      </Grid>
    </div>
  );
};

interface Props extends PropsWithChildren<TableTreeColumn.CellProps> {
  forwardedRef?: ForwardedRef<HTMLTableCellElement>;
}

const BudgetMixTableCell = ({ children, forwardedRef, row }: Props) => (
  <td
    ref={forwardedRef}
    className={clsx(
      "dx-g-bs4-fixed-cell",
      "position-sticky w-full min-w-full cell-clickable group/row",
      { "bg-v2-secondary": !Object.hasOwn(row, "items") },
    )}
    style={{
      padding: "1.6px", // NOTE: Hack to get rows to align...
    }}
  >
    <div className="flex h-full px-3">{children}</div>
  </td>
);
